import React from 'react';
import { ComponentRendering, isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { Element } from 'react-scroll';
import ProductCard from './components/productCard';
import { TModelsListingProps } from './types/modelsListing.types';
import { ModelsListingModel } from './models/modelsListing.model';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import { targetLinkdsk } from '../../utils/targetDskLink';
import { googleClickCardEvent, googleClickCtaEvent } from '../../utils/analytics';
import { getTextType } from '../../utils/getTextType.utils';
import { descriptionKeyModelListing, eyebrowKeyModelListing, titleKeyModelListing } from '../../constants/dictionary.constants';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import RoutableSitecoreLink from '../@shared/routableSitecoreLink';
import Icon from '../@shared/icon';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import TextComponentDecider from '../@shared/TextComponentDecider';

const ProductModelsListing = (props: TModelsListingProps): JSX.Element | null => {
  const apiResponse = isEmptyResponse(props);
  const model = new ModelsListingModel(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const headerButton = model.button1;
  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model?.thumbnailEE} componentName={model?.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model?.componentName} />;
  }

  // No data and not in EE
  if (((!apiResponse && !isPlaceholderShown) || model?.items?.length === 0) && !isEditorActive()) {
    return null;
  }

  return (
    <AnalyticsVisibleEvent rendering={props?.rendering as ComponentRendering} title={model?.title?.value ?? ''} template="Model Listing">
      <Element name="Models">
        <div className="product-card-dashboard__wrapper">
          <div className="product-card-dashboard">
            <div className="product-card__header">
              <TextComponentDecider
                type={getTextType('Text', !model?.eyebrow?.value)}
                tag={'p'}
                field={{ value: model?.eyebrow?.value }}
                className="product-card__header__eyebrow"
                dictionaryKey={eyebrowKeyModelListing}
              />
              <TextComponentDecider
                type={getTextType('Text', !model?.title?.value)}
                tag={'h2'}
                field={{ value: model?.title?.value }}
                className="product-card__header__title"
                dictionaryKey={titleKeyModelListing}
              />

              <TextComponentDecider
                type={getTextType('RichText', !model?.description?.value)}
                tag={'div'}
                field={{ value: model?.description?.value }}
                className="product-card__header__subtitle rich-text"
                dictionaryKey={descriptionKeyModelListing}
              />

              {headerButton?.value?.href && headerButton?.value?.text && (
                <div className={'product-card__link'}>
                  <span className="link-with-arrow link-with-arrow--text">
                    <RoutableSitecoreLink
                      field={headerButton}
                      target={targetLinkdsk(headerButton, isDesktop)}
                      className="product-card__title"
                      onClick={() => googleClickCtaEvent('Link', headerButton.value.text, model?.title?.value)}
                    />
                    <Icon iconName="arrow-simple-right" size={24} color="primary" />
                  </span>
                </div>
              )}
            </div>
            <section className="product-card-dashboard__products">
              {model?.items &&
                model?.items?.map((productCard, index) => (
                  <ProductCard
                    {...productCard}
                    baseUrl={model?.baseUrl}
                    key={productCard?.id}
                    onClick={() => googleClickCardEvent(productCard.title, index + 1)}
                    bodyTextVisibility={model.bodyTextVisibility}
                    bodySpecVisibility={model.bodySpecVisibility}
                  />
                ))}
            </section>
          </div>
        </div>
      </Element>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(ProductModelsListing);
